import Dexie from 'dexie';
import stringify from 'safe-stable-stringify';

const CRYPTO_DB_NAME = 'q1-crypto-db';
const CRYPTO_DB_VERSION = 1;
const CRYPTO_DB_KEY_STORE = 'keyPairStore';
const PRIMARY_KEY_PAIR_NAME = 'primaryKey';

const HASH_ALGO = 'SHA-256';
const SIGN_ALGO = { name: 'ECDSA', hash: HASH_ALGO };
const KEY_GEN_ALGO = { name: 'ECDSA', namedCurve: 'P-256' };
const KEY_GEN_USAGES = ['sign', 'verify'];

const db = new Dexie(CRYPTO_DB_NAME);
db.version(CRYPTO_DB_VERSION).stores({
  [CRYPTO_DB_KEY_STORE]: 'name, generatedAt',
});


/*=== KEY MANAGEMENT ===*/

/**
 * Generates an key pair and stores it directly in IndexedDB.
 * The private key is non-extractable, keeping it secure.
 *
 * @param {string} keyPairName - The name of the key pair.
 * 
 * @returns {Promise<Object>} - Resolves when keypair is stored. Returns the keypair.
 *   With keys `name`, `publicKey`, `privateKey`, `generatedAt`.
 */
const generateAndStoreKeyPair = async (keyPairName = PRIMARY_KEY_PAIR_NAME) => {
  const keyPair = await crypto.subtle.generateKey(KEY_GEN_ALGO, false, KEY_GEN_USAGES);
  const keyPairDbItem = {
    name: keyPairName,
    publicKey: keyPair.publicKey,
    privateKey: keyPair.privateKey,
    generatedAt: new Date().getTime(),
  };

  // Store in IndexedDB
  await db[CRYPTO_DB_KEY_STORE].put(keyPairDbItem);

  return keyPairDbItem;
};

/**
 * Retrieves the stored key pair from IndexedDB.
 *
 * @param {string} keyPairName - The name of the key pair.
 * 
 * @returns {Promise<Object>} with keys `name`, `publicKey`, `privateKey`, `generatedAt`.
 */
const getKeyPair = async (keyPairName = PRIMARY_KEY_PAIR_NAME) => {
  return db.keyPairStore.get(keyPairName);
};

/**
 * Exports a public CryptoKey to a Base64-encoded SPKI string.
 *
 * @param {CryptoKey} publicKey - The public key to export.
 * @returns {Promise<string>} - The public key as a Base64 string.
 */
const exportPublicKeyToSPKI = async (publicKey) => {
  const spkiBuffer = await crypto.subtle.exportKey("spki", publicKey);

  return uint8ArrayToBase64(new Uint8Array(spkiBuffer));
};

/**
 * Converts a Base64-encoded SPKI string into a public CryptoKey.
 *
 * @param {string} spkiBase64 - The public key in SPKI format (Base64 encoded).
 * @param {Object} keyGenAlgo - The key generation algorithm to use.
 * 
 * @returns {Promise<CryptoKey>}
 */
const importPublicSpkiKey = async (spkiBase64, keyGenAlgo = KEY_GEN_ALGO) => {
  const spkiBuffer = base64ToUint8Array(spkiBase64);

  return await crypto.subtle.importKey(
    'spki',
    spkiBuffer,
    keyGenAlgo,
    true,
    ['verify']
  );
};


/*=== HASHING & SIGNING ===*/

/**
 * Generates a hash digest for a given string value.
 *
 * @param {string} stringValue - The string to hash.
 * @param {string} hashAlgo - The hash algorithm to use.
 * 
 * @returns {Promise<string>} - The generated hash as a hexadecimal string.
 */
const generateHash = async (stringValue, hashAlgo = HASH_ALGO) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(stringValue);
  const hash = await crypto.subtle.digest(hashAlgo, data);

  return uint8ArrayToBase64(new Uint8Array(hash));
}

/**
 * Generates a cryptographic signature for a given object using the stored private key.
 *
 * @param {Object} data - The data to sign.
 * @param {CryptoKey} privateKey - The private key for signing.
 * @param {Object} signAlgo - The signing algorithm to use.
 * 
 * @returns {Promise<String>} - The generated signature (Base64 encoded)
 */
const generateSignedHash = async (data, privateKey, signAlgo = SIGN_ALGO) => {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(stringify(data));

  const signature = await crypto.subtle.sign(signAlgo, privateKey, dataBuffer);

  return uint8ArrayToBase64(new Uint8Array(signature));
};

/**
 * Verifies a cryptographic signature against the given object.
 *
 * @param {string} signature - The signature to verify (Base64 encoded).
 * @param {CryptoKey} publicKey - The public key for verification.
 * @param {Object} originalData - The original data.
 * 
 * @returns {Promise<boolean>} - Whether the signature is valid.
 */
const verifySignedHash = async (signature, publicKey, originalData) => {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(stringify(originalData));
  const signatureBuffer = base64ToUint8Array(signature);

  return await crypto.subtle.verify(SIGN_ALGO, publicKey, signatureBuffer, dataBuffer);
};


/*=== CONVERSION UTILITIES ===*/

/**
 * Converts a Uint8Array to a Base64 encoded string.
 *
 * @param {Uint8Array} uint8Array - The input Uint8Array.
 * 
 * @returns {string} - Base64 encoded string.
 */
const uint8ArrayToBase64 = (uint8Array) => {
  return btoa(String.fromCharCode(...uint8Array));
};

/**
 * Converts a Base64 encoded string to a Uint8Array.
 *
 * @param {string} base64String - The Base64 encoded string.
 * @returns {Uint8Array} - Decoded Uint8Array.
 */
const base64ToUint8Array = (base64String) => {
  const binaryString = atob(base64String);
  return new Uint8Array([...binaryString].map(char => char.charCodeAt(0)));
};


export {
  // Key Management
  generateAndStoreKeyPair,
  getKeyPair,
  exportPublicKeyToSPKI,
  importPublicSpkiKey,

  // Hashing & Signing
  generateHash,
  generateSignedHash,
  verifySignedHash,

  // Conversion Utilities
  uint8ArrayToBase64,
  base64ToUint8Array,
};
